import React, { useEffect, useMemo, useState } from 'react';
import safeToast from '../../utils/safeToast';
import { listBulkRequests, reviewBulkRequest, BulkRequest } from '../../api/bulkRequests';
import { processApprovedBulkRequests } from '../../api/communications';
import { isSuperAdmin } from '../../utils/permissions';
import { getCurrentUser } from '../../api/auth';

export default function SystemAdminRequestsPanel({ onBackToAdmin }: { onBackToAdmin?: () => void }) {
  const [requests, setRequests] = useState<BulkRequest[]>([]);
  const [processing, setProcessing] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<BulkRequest | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [lastError, setLastError] = useState<string | null>(null);
  const STORAGE_KEY = 'admin_bulk_approvals_filters_v1';
  const [bulkType, setBulkType] = useState<'sms' | 'email'>(() => {
    try { return (JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}').bulkType) || 'sms'; } catch { return 'sms'; }
  });
  const [status, setStatus] = useState<'all' | 'pending' | 'approved' | 'rejected'>(() => {
    try { return (JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}').status) || 'pending'; } catch { return 'pending'; }
  });
  const [q, setQ] = useState<string>(() => {
    try { return (JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}').q) || ''; } catch { return ''; }
  });
  const [from, setFrom] = useState<string>(() => {
    try { return (JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}').from) || ''; } catch { return ''; }
  });
  const [to, setTo] = useState<string>(() => {
    try { return (JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}').to) || ''; } catch { return ''; }
  });
  const [page, setPage] = useState<number>(() => {
    try { return Number(JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}').page) || 1; } catch { return 1; }
  });
  const [pageSize, setPageSize] = useState<number>(() => {
    try { return Number(JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}').pageSize) || 25; } catch { return 25; }
  });
  const [total, setTotal] = useState<number>(0);
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / (pageSize || 1))), [total, pageSize]);

  const persist = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ bulkType, status, q, from, to, page, pageSize }));
    } catch {}
  };

  const clearFilters = () => {
    setStatus('pending');
    setQ('');
    setFrom('');
    setTo('');
    setPage(1);
    setPageSize(25);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ bulkType, status: 'pending', q: '', from: '', to: '', page: 1, pageSize: 25 })); } catch {}
  };

  const load = async () => {
    try {
      setForbidden(false);
      setLastError(null);
      const resp = await listBulkRequests({ status, q, from, to, page, limit: pageSize, type: bulkType });
      setRequests(resp.items || []);
      setTotal(resp.total || 0);
    } catch (e) {
      console.error(e);
      const err: any = e;
      if (err?.status === 403) {
        setForbidden(true);
      } else {
        setLastError(err?.message || 'Failed to load requests');
      }
      safeToast('error', 'Failed to load requests');
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    (async () => {
      try { const me = await getCurrentUser(); setCurrentUser(me); } catch (e) {}
    })();
  }, []);

  useEffect(() => {
    load();
    persist();
  }, [bulkType, status, q, from, to, page, pageSize]);

  const review = async (id: string, action: 'approve' | 'reject') => {
    try {
      const updated = await reviewBulkRequest(id, action);
  setRequests((s: BulkRequest[]) => s.map((r: BulkRequest) => (r.id === updated.id ? { ...r, status: updated.status, reviewedBy: updated.reviewedBy } : r)));
      safeToast('success', `Request ${action}ed`);
      if (detail && detail.id === id) { setDetail({ ...detail, status: updated.status, reviewedBy: updated.reviewedBy }); }
    } catch (e) {
      console.error(e);
      safeToast('error', `Failed to ${action}`);
    }
  };

  const processNow = async () => {
    if (!isSuperAdmin(currentUser)) return safeToast('error', 'Only Super Admin can process approved requests');
    setProcessing(true);
    try {
  const r = await processApprovedBulkRequests(25);
      safeToast('success', r?.ok ? 'Processing approved requests…' : 'Triggered processing');
    } catch (e: any) {
      console.error(e);
      safeToast('error', `Failed to process: ${e?.message || e}`);
    } finally { setProcessing(false); }
  };

  const filtered = requests; // server-side filtered

  return (
    <div className="p-4">
      {forbidden && (
        <div className="mb-3 p-3 rounded border border-yellow-300 bg-yellow-50 text-sm text-yellow-900">
          This panel requires Super Admin privileges. Please log in as a Super Admin to view and approve bulk requests.
        </div>
      )}
      {!!lastError && !forbidden && (
        <div className="mb-3 p-3 rounded border border-red-300 bg-red-50 text-sm text-red-800">
          {lastError}
        </div>
      )}
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold">Bulk Requests</h2>
            <p className="text-sm text-gray-600">Choose a type and take action on queued requests</p>
          </div>
          <div className="flex items-center gap-2">
            {isSuperAdmin(currentUser) && (
              <button onClick={processNow} disabled={processing} className="px-3 py-2 bg-gray-900 text-white rounded disabled:opacity-60 whitespace-nowrap">
                {processing ? 'Processing…' : 'Process approved now'}
              </button>
            )}
            {onBackToAdmin && (
              <button onClick={() => onBackToAdmin()} className="px-3 py-2 bg-gray-600 text-white rounded whitespace-nowrap">Back</button>
            )}
          </div>
        </div>

        {/* Type segmented control + filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-600">Type</span>
          <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
            <button onClick={() => { setPage(1); setBulkType('sms'); }} className={`px-4 py-1.5 text-sm rounded-md ${bulkType==='sms' ? 'bg-white border border-gray-200 shadow-sm' : 'text-gray-600'}`} aria-pressed={bulkType==='sms'}>SMS</button>
            <button onClick={() => { setPage(1); setBulkType('email'); }} className={`px-4 py-1.5 text-sm rounded-md ${bulkType==='email' ? 'bg-white border border-gray-200 shadow-sm' : 'text-gray-600'}`} aria-pressed={bulkType==='email'}>Email</button>
          </div>
          <div className="hidden sm:flex flex-wrap items-center gap-2 ml-auto max-w-full overflow-x-auto">
            <select value={status} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setPage(1); setStatus(e.target.value as any); }} className="px-2 py-1 border rounded text-sm">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="all">All</option>
            </select>
            <input value={q} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPage(1); setQ(e.target.value); }} placeholder="Search subject/title/creator" className="px-2 py-1 border rounded text-sm w-52" />
            <input type="date" value={from} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPage(1); setFrom(e.target.value); }} className="px-2 py-1 border rounded text-sm w-36" />
            <span className="text-gray-500">to</span>
            <input type="date" value={to} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPage(1); setTo(e.target.value); }} className="px-2 py-1 border rounded text-sm w-36" />
            <select value={String(pageSize)} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setPage(1); setPageSize(Number(e.target.value)); }} className="px-2 py-1 border rounded text-sm">
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
            <button onClick={clearFilters} className="px-2 py-1 border rounded text-sm text-gray-700">Clear</button>
          </div>
          {/* Filter summary for quick visibility */}
          <div className="w-full text-xs text-gray-500">
            Showing {bulkType.toUpperCase()} • {status === 'all' ? 'all statuses' : status}
            {q ? ` • q:"${q}"` : ''}
            {from ? ` • from:${from}` : ''}
            {to ? ` • to:${to}` : ''}
            {pageSize ? ` • page size:${pageSize}` : ''}
          </div>
        </div>
      </div>

      {/* Mobile filters */}
      <div className="sm:hidden mb-3 space-y-2">
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1 w-full">
          <button onClick={() => { setPage(1); setBulkType('sms'); }} className={`flex-1 px-4 py-1.5 text-sm rounded-md ${bulkType==='sms' ? 'bg-white border border-gray-200 shadow-sm' : 'text-gray-600'}`}>SMS</button>
          <button onClick={() => { setPage(1); setBulkType('email'); }} className={`flex-1 px-4 py-1.5 text-sm rounded-md ${bulkType==='email' ? 'bg-white border border-gray-200 shadow-sm' : 'text-gray-600'}`}>Email</button>
        </div>
        <div className="flex gap-2">
          <select value={status} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setPage(1); setStatus(e.target.value as any); }} className="px-2 py-1 border rounded text-sm flex-1">
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="all">All</option>
          </select>
          <select value={String(pageSize)} onChange={(e: React.ChangeEvent<HTMLSelectElement>) => { setPage(1); setPageSize(Number(e.target.value)); }} className="px-2 py-1 border rounded text-sm">
            <option value="10">10</option>
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
  <input value={q} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPage(1); setQ(e.target.value); }} placeholder="Search subject/title/creator" className="px-2 py-1 border rounded text-sm w-full" />
        <div className="flex items-center gap-2">
          <input type="date" value={from} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPage(1); setFrom(e.target.value); }} className="px-2 py-1 border rounded text-sm flex-1" />
          <span className="text-gray-500">to</span>
          <input type="date" value={to} onChange={(e: React.ChangeEvent<HTMLInputElement>) => { setPage(1); setTo(e.target.value); }} className="px-2 py-1 border rounded text-sm flex-1" />
        </div>
        <button onClick={clearFilters} className="px-2 py-1 border rounded text-sm text-gray-700 w-full">Clear filters</button>
      </div>

    {filtered.length === 0 ? (
        <div className="text-sm text-gray-600 border rounded p-4 bg-gray-50">
      <div className="font-medium mb-1">No {bulkType.toUpperCase()} {status !== 'all' ? status : ''} requests found.</div>
          <div className="flex items-center gap-2">
            <span>Try adjusting the filters</span>
            <button onClick={clearFilters} className="px-2 py-1 border rounded text-xs text-gray-700">Clear filters</button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((r) => (
            <div key={r.id} className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wide text-gray-500">{r.type === 'sms' ? 'SMS' : 'Email'}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${r.status==='pending' ? 'bg-yellow-100 text-yellow-800' : r.status==='approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{r.status}</span>
                  </div>
                  {r.payload?.title && <div className="mt-1 font-medium text-gray-900">{r.payload.title}</div>}
                  {r.payload?.subject && <div className="mt-1 font-medium text-gray-900">{r.payload.subject}</div>}
                  <div className="mt-2 text-sm text-gray-700 line-clamp-3 whitespace-pre-wrap">{r.payload?.message}</div>
                  <div className="mt-3 text-xs text-gray-500">
                    <span>Recipients: {r.payload?.recipients?.length ?? 0}</span>
                    <span className="mx-2">•</span>
                    <span>By: {r.createdByEmail || r.createdById}</span>
                    {r.reviewedBy && (
                      <>
                        <span className="mx-2">•</span>
                        <span>Reviewed by: {r.reviewedBy}</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 ml-4">
                  {r.status === 'pending' ? (
                    isSuperAdmin(currentUser) ? (
                      <>
                        <button onClick={() => review(r.id, 'approve')} className="px-3 py-1.5 bg-green-600 text-white rounded w-28">Approve</button>
                        <button onClick={() => review(r.id, 'reject')} className="px-3 py-1.5 bg-red-600 text-white rounded w-28">Reject</button>
                      </>
                    ) : (
                      <div className="text-xs text-gray-500">Pending review by System Admin</div>
                    )
                  ) : (
                    <div className="text-xs text-gray-500">No actions</div>
                  )}
                  <button onClick={() => { setDetail(r); setDetailOpen(true); }} className="w-full mt-2 text-xs text-gray-700 underline">View full</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">Total: {total} • Page {page} of {totalPages}</div>
        <div className="flex items-center gap-2">
          <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage(1)} disabled={page <= 1}>First</button>
          <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>Prev</button>
          <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}>Next</button>
          <button className="px-3 py-1 border rounded disabled:opacity-50" onClick={() => setPage(totalPages)} disabled={page >= totalPages}>Last</button>
        </div>
      </div>

      {/* Details Modal */}
      <BulkDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        item={detail}
        onApprove={(id) => review(id, 'approve')}
        onReject={(id) => review(id, 'reject')}
        canReview={isSuperAdmin(currentUser)}
      />
    </div>
  );
}

// Inline modal to read full content and recipients
export function BulkDetailModal({ open, onClose, item, onApprove, onReject, canReview }: {
  open: boolean;
  onClose: () => void;
  item: BulkRequest | null;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  canReview?: boolean;
}) {
  if (!open || !item) return null;
  const recipients: any[] = Array.isArray(item.payload?.recipients) ? item.payload?.recipients : [];
  const [showAll, setShowAll] = React.useState(false);
  const preview = showAll ? recipients : recipients.slice(0, 50);
  const extra = Math.max(0, recipients.length - preview.length);

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full sm:w-[560px] bg-white shadow-xl flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500">{item.type === 'sms' ? 'SMS' : 'Email'}</div>
            <div className="text-lg font-semibold">{item.payload?.title || item.payload?.subject || 'Bulk Request'}</div>
          </div>
          <button onClick={onClose} className="px-2 py-1 text-gray-600 hover:text-black">✕</button>
        </div>

        <div className="p-4 overflow-auto grow">
          <div className="mb-4">
            <div className="text-sm font-medium text-gray-900">Message</div>
            <pre className="mt-1 whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 p-3 rounded max-h-64 overflow-auto">{item.payload?.message || '(no message)'}</pre>
          </div>

          <div className="mb-4">
            <div className="text-sm font-medium text-gray-900">Recipients ({recipients.length})</div>
            {recipients.length === 0 ? (
              <div className="text-sm text-gray-600 mt-1">No explicit recipients listed. Targets may be resolved server-side (e.g., cohorts or all leads).</div>
            ) : (
              <div className="mt-2 border rounded bg-white max-h-56 overflow-auto">
                <ul className="text-sm text-gray-800 divide-y">
                  {preview.map((rcp, idx) => (
                    <li key={idx} className="px-3 py-1.5 truncate" title={String(rcp)}>{String(rcp)}</li>
                  ))}
                </ul>
              </div>
            )}
            {extra > 0 && (
              <button onClick={() => setShowAll((s) => !s)} className="mt-2 text-xs text-blue-700 underline">
                {showAll ? 'Show less' : `Show all (+${extra})`}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-700">
            <div><span className="text-gray-500">Created by:</span> {item.createdByEmail || item.createdById}</div>
            <div><span className="text-gray-500">Status:</span> {item.status}</div>
          </div>
        </div>

        <div className="p-4 border-t flex items-center gap-2 justify-end">
          {item.status === 'pending' ? (
            canReview ? (
              <>
                <button onClick={() => onApprove(item.id)} className="px-4 py-2 bg-green-600 text-white rounded">Approve</button>
                <button onClick={() => onReject(item.id)} className="px-4 py-2 bg-red-600 text-white rounded">Reject</button>
              </>
            ) : (
              <button onClick={onClose} className="px-4 py-2 border rounded">Close</button>
            )
          ) : (
            <button onClick={onClose} className="px-4 py-2 border rounded">Close</button>
          )}
        </div>
      </div>
    </div>
  );
}
